import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Drawer,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  List,
  ListItem,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Close as CloseIcon,
  FullscreenExit as FullscreenExitIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MapView from '../components/MapView';
import { MapSearch } from '../components/MapSearch';
import { cpeService } from '../services/cpeService';
import zoneService from '../services/zoneService';
import { useAuth } from '../contexts/AuthContext';
import {
  CPE,
  CPEFilters,
  PlantType,
  InternalPositioning,
  CPEStatus,
  CommercialStatus,
  PLANT_TYPES,
  CPE_STATUSES,
  COMMERCIAL_STATUSES,
  NextActionOption,
} from '../types/cpe';
import nextActionOptionService from '../services/nextActionOptionService';
import { Zone } from '../types/zone';
import { CPEFormDialog } from '../components/CPEFormDialog';
import { User } from '../types/user';
import { userService } from '../services/userService';
import { useMapFilters } from '../hooks/useMapFilters';

const MapPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cpes, setCPEs] = useState<CPE[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Use persistent filter hook
  const {
    filters,
    setFilters,
    visibleZones,
    setVisibleZones,
    mapDarkMode,
    setMapDarkMode,
    clearFilters,
    areZonesInitialized,
  } = useMapFilters();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formInitialLat, setFormInitialLat] = useState<string>();
  const [formInitialLng, setFormInitialLng] = useState<string>();
  const [selectedCPEId, setSelectedCPEId] = useState<string | null>(null); // For PP selection from search
  const [zoneDrawerOpen, setZoneDrawerOpen] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [nextActionOptions, setNextActionOptions] = useState<NextActionOption[]>([]);

  // Map control for search functionality
  const mapControlRef = React.useRef<{ navigateTo: (lat: number, lng: number, zoom?: number) => void }>({
    navigateTo: () => {},
  });

  const canAddCPE = user?.role === 'admin' || user?.role === 'super_admin';

  const loadCPEs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Load all CPEs without pagination for map display
      const response = await cpeService.getCPEs(1, 10000, filters);
      setCPEs(response.data.cpes);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      console.error('Failed to load CPEs:', err);
      setError(error.response?.data?.message || 'Failed to load CPEs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCPEs();
  }, [loadCPEs]);

  // Load zones for visibility toggles
  const loadZones = useCallback(async () => {
    try {
      const zonesList = await zoneService.getZonesList();
      setZones(zonesList);
      // Only initialize visible zones on first visit (not when user has already configured)
      if (!areZonesInitialized()) {
        const visibleIds = new Set(zonesList.filter(z => z.is_visible).map(z => z.id));
        setVisibleZones(visibleIds);
      }
    } catch (error) {
      console.error('Error loading zones:', error);
    }
  }, [areZonesInitialized, setVisibleZones]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  // Load users for account manager filter
  const loadUsers = useCallback(async () => {
    try {
      const usersList = await userService.getAllUsers();
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Load next action options for filter
  const loadNextActionOptions = useCallback(async () => {
    try {
      const options = await nextActionOptionService.getAll();
      setNextActionOptions(options);
    } catch (error) {
      console.error('Error loading next action options:', error);
    }
  }, []);

  useEffect(() => {
    loadNextActionOptions();
  }, [loadNextActionOptions]);

  const handleCPEClick = (cpe: CPE) => {
    navigate(`/cpes/${cpe.id}`);
  };

  const handleFormClose = () => {
    setFormDialogOpen(false);
    setFormInitialLat(undefined);
    setFormInitialLng(undefined);
    // Reload CPEs after closing dialog
    loadCPEs();
  };

  const handleFilterChange = (field: keyof CPEFilters, value: string | number | PlantType[] | CPEStatus[] | CommercialStatus[] | InternalPositioning[] | string[] | boolean | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handlePlantTypeChange = (event: SelectChangeEvent<PlantType[]>) => {
    const value = event.target.value;
    handleFilterChange('plant_type', typeof value === 'string' ? value.split(',') as PlantType[] : value);
  };


  const toggleZoneVisibility = (zoneId: string) => {
    const newVisibleZones = new Set(visibleZones);
    if (newVisibleZones.has(zoneId)) {
      newVisibleZones.delete(zoneId);
    } else {
      newVisibleZones.add(zoneId);
    }
    setVisibleZones(newVisibleZones);
  };

  const showAllZones = () => {
    const allZoneIds = new Set(zones.map(z => z.id));
    setVisibleZones(allZoneIds);
  };

  const hideAllZones = () => {
    setVisibleZones(new Set());
  };

  const activeFilterCount = Object.keys(filters).filter(
    (key) => filters[key as keyof CPEFilters] !== undefined
  ).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Map with Overlay Controls */}
      <Box sx={{ flex: 1, position: 'relative', height: '100%' }}>
        {/* Search Button - Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1500, // Higher than other buttons to allow dropdown to appear on top
          }}
        >
          <MapSearch
            cpes={cpes}
            onLocationSelect={(lat, lng) => {
              mapControlRef.current.navigateTo(lat, lng, 10);
            }}
            onPPSelect={(ppId, lat, lng) => {
              // Navigate to the PP location
              mapControlRef.current.navigateTo(lat, lng, 10);
              // Select the PP to enlarge pin and show tooltip
              setSelectedCPEId(ppId);
            }}
          />
        </Box>

        {/* Exit Full Screen Button - Below Search */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 60,
            right: 10,
            zIndex: 1000,
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            color: 'text.primary',
            border: theme => `1px solid ${theme.palette.divider}`,
            '&:hover': {
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
            },
          }}
          onClick={() => navigate('/')}
          title="Exit Full Screen"
        >
          <FullscreenExitIcon />
        </IconButton>

        {/* Filters Button - Below Exit button (same vertical stack) */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 110, // Below the exit button
            right: 10,
            zIndex: 1000,
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            color: 'text.primary',
            border: theme => `1px solid ${theme.palette.divider}`,
            '&:hover': {
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
            },
          }}
          onClick={() => setFilterDrawerOpen(true)}
          title="Filters"
        >
          <FilterListIcon />
          {activeFilterCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 'bold',
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </IconButton>

        {/* Map Theme Toggle Button - Below Filter */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 160, // Below the filter button
            right: 10,
            zIndex: 1000,
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            color: 'text.primary',
            border: theme => `1px solid ${theme.palette.divider}`,
            '&:hover': {
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
            },
          }}
          onClick={() => setMapDarkMode(!mapDarkMode)}
          title={mapDarkMode ? "Light Map Theme" : "Dark Map Theme"}
        >
          {mapDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>

        {/* Zones/Layers Button - Below Map Theme */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 210, // Below the map theme button
            right: 10,
            zIndex: 1000,
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            color: 'text.primary',
            border: theme => `1px solid ${theme.palette.divider}`,
            '&:hover': {
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
            },
          }}
          onClick={() => setZoneDrawerOpen(true)}
          title={t('zones.toggleVisibility', 'Toggle Zone Visibility')}
        >
          <LayersIcon />
        </IconButton>

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && (
          <MapView
            cpes={cpes}
            mapDarkMode={mapDarkMode}
            onCPEClick={handleCPEClick}
            canAddCPE={canAddCPE}
            mapControl={mapControlRef.current}
            selectedCPEId={selectedCPEId}
            onClearSelection={() => setSelectedCPEId(null)}
            zones={zones}
            visibleZones={visibleZones}
            onCreated={() => {
              loadCPEs();
              loadZones();
            }}
          />
        )}
      </Box>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">{t('filters.title')}</Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Search */}
          <TextField
            fullWidth
            label={t('filters.searchPlaceholder')}
            placeholder={t('filters.searchPlaceholder')}
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Plant Type */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.plantType')}</InputLabel>
            <Select
              multiple
              value={filters.plant_type || []}
              onChange={handlePlantTypeChange}
              input={<OutlinedInput label={t('cpe.fields.plantType')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                );
              }}
            >
              {PLANT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={(filters.plant_type || []).indexOf(type) > -1} />
                  <ListItemText primary={type} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* CPE Status */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.cpeStatus')}</InputLabel>
            <Select
              multiple
              value={filters.cpe_status || []}
              onChange={(e) => handleFilterChange('cpe_status', e.target.value)}
              input={<OutlinedInput label={t('cpe.fields.cpeStatus')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                );
              }}
            >
              {CPE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={(filters.cpe_status || []).indexOf(status) > -1} />
                  <ListItemText primary={status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Commercial Status */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.commercialStatus')}</InputLabel>
            <Select
              multiple
              value={filters.commercial_status || []}
              onChange={(e) => handleFilterChange('commercial_status', e.target.value)}
              input={<OutlinedInput label={t('cpe.fields.commercialStatus')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                );
              }}
            >
              {COMMERCIAL_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={(filters.commercial_status || []).indexOf(status) > -1} />
                  <ListItemText primary={status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Internal Positioning */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.internalPositioning')}</InputLabel>
            <Select
              multiple
              value={filters.internal_positioning || []}
              onChange={(e) => handleFilterChange('internal_positioning', e.target.value as InternalPositioning[])}
              input={<OutlinedInput label={t('cpe.fields.internalPositioning')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip
                        key={value}
                        label={value === 'go' ? t('cpe.positioning.go') : t('cpe.positioning.noGo')}
                        size="small"
                      />
                    ))}
                  </Box>
                );
              }}
            >
              <MenuItem value="go">
                <Checkbox checked={(filters.internal_positioning || []).indexOf('go') > -1} />
                <ListItemText primary={t('cpe.positioning.go')} />
              </MenuItem>
              <MenuItem value="no_go">
                <Checkbox checked={(filters.internal_positioning || []).indexOf('no_go') > -1} />
                <ListItemText primary={t('cpe.positioning.noGo')} />
              </MenuItem>
            </Select>
          </FormControl>

          {/* Power Range */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label={t('filters.minPower')}
              type="number"
              value={filters.min_power || ''}
              onChange={(e) => handleFilterChange('min_power', e.target.value ? Number(e.target.value) : undefined)}
              fullWidth
            />
            <TextField
              label={t('filters.maxPower')}
              type="number"
              value={filters.max_power || ''}
              onChange={(e) => handleFilterChange('max_power', e.target.value ? Number(e.target.value) : undefined)}
              fullWidth
            />
          </Box>

          {/* Energization Year */}
          <TextField
            fullWidth
            label={t('cpe.fields.energizationYear')}
            type="number"
            value={filters.energization_year || ''}
            onChange={(e) => handleFilterChange('energization_year', e.target.value ? Number(e.target.value) : undefined)}
            sx={{ mb: 2 }}
          />

          {/* Account Manager */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel shrink>{t('cpe.fields.omAccountManager')}</InputLabel>
            <Select
              multiple
              value={filters.om_account_manager ? (Array.isArray(filters.om_account_manager) ? filters.om_account_manager : [filters.om_account_manager]) : []}
              onChange={(e) => {
                const selected = e.target.value as string[];
                handleFilterChange('om_account_manager', selected.length > 0 ? selected : undefined);
              }}
              input={<OutlinedInput label={t('cpe.fields.omAccountManager')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                const selectedIds = selected as string[];
                const selectedNames = selectedIds.map(id => users.find(u => u.id === id)?.name || users.find(u => u.id === id)?.email).filter(Boolean);
                return selectedNames.join(', ');
              }}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Checkbox checked={(filters.om_account_manager as string[])?.includes(user.id) || false} />
                  <ListItemText primary={user.name || user.email} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Waldevar PP */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.isWaldevarPP', 'Waldevar PP')}</InputLabel>
            <Select
              value={filters.is_waldevar_pp !== undefined ? String(filters.is_waldevar_pp) : ''}
              onChange={(e) => {
                const val = e.target.value as string;
                handleFilterChange('is_waldevar_pp', val === '' ? undefined : val === 'true');
              }}
              displayEmpty
              input={<OutlinedInput label={t('cpe.fields.isWaldevarPP', 'Waldevar PP')} notched />}
            >
              <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
              <MenuItem value="true">{t('common.yes')}</MenuItem>
              <MenuItem value="false">{t('common.no')}</MenuItem>
            </Select>
          </FormControl>

          {/* Zone */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel shrink>{t('filters.zone')}</InputLabel>
            <Select
              multiple
              value={filters.zone_id ? (Array.isArray(filters.zone_id) ? filters.zone_id : [filters.zone_id]) : []}
              onChange={(e) => {
                const selected = e.target.value as string[];
                handleFilterChange('zone_id', selected.length > 0 ? selected : undefined);
              }}
              input={<OutlinedInput label={t('filters.zone')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                const selectedIds = selected as string[];
                const selectedNames = selectedIds.map(id => zones.find(z => z.id === id)?.name).filter(Boolean);
                return selectedNames.join(', ');
              }}
            >
              {zones.map((zone) => (
                <MenuItem key={zone.id} value={zone.id}>
                  <Checkbox checked={(filters.zone_id as string[])?.includes(zone.id) || false} />
                  <ListItemText primary={zone.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Next Action Filter */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel shrink>{t('nextAction.label', 'Next Action')}</InputLabel>
            <Select
              value={filters.next_action_id || ''}
              onChange={(e) => {
                const val = e.target.value || undefined;
                setFilters((prev) => ({ ...prev, next_action_id: val, overdue_next_action: undefined }));
              }}
              displayEmpty
              input={<OutlinedInput label={t('nextAction.label', 'Next Action')} notched />}
            >
              <MenuItem value=""><em>{t('nextAction.filter.all', 'All')}</em></MenuItem>
              <MenuItem value="none">{t('nextAction.filter.none', 'No action set')}</MenuItem>
              {nextActionOptions
                .filter((o) => o.is_active || o.id === filters.next_action_id)
                .map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Overdue Next Action toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Chip
              label={t('nextAction.filter.overdue', 'Overdue Only')}
              color={filters.overdue_next_action ? 'error' : 'default'}
              variant={filters.overdue_next_action ? 'filled' : 'outlined'}
              onClick={() => {
                handleFilterChange('overdue_next_action', filters.overdue_next_action ? undefined : true);
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" fullWidth onClick={clearFilters}>
              {t('filters.clearFilters')}
            </Button>
            <Button variant="contained" fullWidth onClick={() => setFilterDrawerOpen(false)}>
              {t('common.close')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Zone Visibility Drawer */}
      <Drawer
        anchor="right"
        open={zoneDrawerOpen}
        onClose={() => setZoneDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">{t('zones.title', 'Zones')}</Typography>
            <IconButton onClick={() => setZoneDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('zones.toggleDescription', 'Toggle zone visibility on the map')}
          </Typography>

          {/* Show All / Hide All Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={showAllZones}
            >
              {t('zones.showAll', 'Show All')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={hideAllZones}
            >
              {t('zones.hideAll', 'Hide All')}
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Zone List */}
          {zones.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('zones.noZones', 'No zones available')}
            </Typography>
          ) : (
            <List>
              {zones.map((zone) => (
                <ListItem
                  key={zone.id}
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      checked={visibleZones.has(zone.id)}
                      onChange={() => toggleZoneVisibility(zone.id)}
                    />
                  }
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: zone.color,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                        <Typography variant="body2">{zone.name}</Typography>
                      </Box>
                    }
                    secondary={`${(zone.radius_meters / 1000).toFixed(1)} km radius`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Actions */}
          <Button
            variant="contained"
            fullWidth
            onClick={() => setZoneDrawerOpen(false)}
          >
            {t('common.close')}
          </Button>
        </Box>
      </Drawer>

      {/* CPE Form Dialog */}
      <CPEFormDialog
        open={formDialogOpen}
        onClose={handleFormClose}
        initialLatitude={formInitialLat}
        initialLongitude={formInitialLng}
      />
    </Box>
  );
};

export default MapPage;

